import React, { useState, useEffect, useMemo } from "react";
import Avatar from "../../components/ui/Avatar";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Toast from "../../components/ui/Toast";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import weekScheduleService from "../../api/weekScheduleService";
import { formatDateLocal, parseDateLocal } from "../../utils/dateUtils";
import {
  isHoliday as isDayHoliday,
  getEgyptHolidayName,
} from "../../utils/holidays";
import requestService from "../../api/requestService";
import RandomWFHModal from "../../components/modals/RandomWFHModal";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  RefreshCw,
  Shuffle,
  Home,
} from "lucide-react";

const WeekSchedule = () => {
  const navigate = useNavigate();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { auth } = useAuth();
  const employee = auth?.employee;

  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [showRandomWFHModal, setShowRandomWFHModal] = useState(false);

  const employeesWithoutWFH = useMemo(() => {
    if (!scheduleData || !scheduleData.employees) return [];

    return scheduleData.employees.filter((emp) => {
      const dayValues = Object.values(emp.days || {});
      return !dayValues.some((value) => value === "WFH");
    });
  }, [scheduleData]);

  // Get current week start (Sunday)
  const getCurrentWeekStart = () => {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  };

  const formatDateForAPI = (date) => formatDateLocal(date);

  // Fetch schedule data
  const fetchSchedule = async (weekStart = null) => {
    try {
      setLoading(true);
      const weekStartStr = weekStart ? formatDateForAPI(weekStart) : null;
      const response = await weekScheduleService.getWeekSchedule(weekStartStr);

      setScheduleData(response.data);

      // Set current week start from response (parse as local date to avoid UTC shift)
      if (response.data && response.data.weekStart) {
        setCurrentWeekStart(parseDateLocal(response.data.weekStart));
      }
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
      showError(error.response?.data?.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const weekStart = getCurrentWeekStart();
    setCurrentWeekStart(weekStart);
    fetchSchedule(weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to previous week
  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
    fetchSchedule(newWeekStart);
  };

  // Navigate to next week
  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
    fetchSchedule(newWeekStart);
  };

  // Navigate to current week
  const handleCurrentWeek = () => {
    const weekStart = getCurrentWeekStart();
    setCurrentWeekStart(weekStart);
    fetchSchedule(weekStart);
  };

  // Check if currently viewing current week
  const isCurrentWeek = () => {
    if (!currentWeekStart) return false;
    const now = getCurrentWeekStart();
    return currentWeekStart.getTime() === now.getTime();
  };

  // Navigate to Requests page to add request
  const handleAddRequest = () => {
    navigate("/management/requests", { state: { openAddModal: true } });
  };

  // Handle random WFH generation
  const handleGenerateRandomWFH = async (data) => {
    try {
      const response = await requestService.generateRandomWFH(data);
      showSuccess(
        response.message || "Random WFH requests generated successfully",
      );

      // Refresh schedule to show new pending requests (they need approval first)
      fetchSchedule(currentWeekStart);
    } catch (error) {
      console.error("Failed to generate random WFH:", error);
      showError(
        error.response?.data?.message || "Failed to generate WFH requests",
      );
      throw error; // Re-throw to let modal handle it
    }
  };

  // Local YYYY-MM-DD of today, to tint today's column.
  const nowDate = new Date();
  const todayKey = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}-${String(nowDate.getDate()).padStart(2, "0")}`;
  const columnTint = (day, isHoliday) =>
    day.date === todayKey ? "bg-accent-soft" : isHoliday ? "bg-danger-soft" : "";

  // Get cell styling based on value and whether day is a holiday (weekend or Egypt public holiday)
  const getCellStyle = (value, isHoliday = false, day = null) =>
    `px-4 py-3.5 text-center text-sm ${day ? columnTint(day, isHoliday) : isHoliday ? "bg-danger-soft" : ""}`;

  // Get badge for special values
  const renderCellValue = (value, isHoliday = false) => {
    if (value === "WFH") {
      return (
        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-ok-soft text-ok text-xs font-medium">
          WFH
        </span>
      );
    }

    if (value === "Vacation") {
      return (
        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-danger-soft text-danger text-xs font-medium">
          Vacation
        </span>
      );
    }

    if (value === "Mission") {
      return (
        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-accent-soft text-accent-ink text-xs font-medium">
          Mission
        </span>
      );
    }

    if (value === "Late") {
      return (
        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-warn-soft text-warn text-xs font-medium">
          Late
        </span>
      );
    }

    if (value === "Early") {
      return (
        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-warn-soft text-warn text-xs font-medium">
          Early
        </span>
      );
    }

    // For holidays show "Holiday", for regular days with no special status show "---"
    if (isHoliday) {
      return (
        <span className="text-danger font-medium text-xs">
          Holiday
        </span>
      );
    }

    return (
      <span className="text-faint">—</span>
    );
  };

  // Compact badge renderer for mobile card view
  const renderCellValueMobile = (value, isHoliday = false) => {
    const cls = "inline-block px-1.5 py-0.5 rounded text-[10px] font-bold";
    if (value === "WFH")
      return (
        <span
          className={`${cls} bg-ok-soft text-ok`}
        >
          WFH
        </span>
      );
    if (value === "Vacation")
      return (
        <span
          className={`${cls} bg-danger-soft text-danger`}
        >
          Vac
        </span>
      );
    if (value === "Mission")
      return (
        <span
          className={`${cls} bg-accent-soft text-accent-ink`}
        >
          Mis
        </span>
      );
    if (value === "Late")
      return (
        <span
          className={`${cls} bg-warn-soft text-warn`}
        >
          Late
        </span>
      );
    if (value === "Early")
      return (
        <span
          className={`${cls} bg-warn-soft text-warn`}
        >
          Early
        </span>
      );
    if (isHoliday)
      return (
        <span className="text-[10px] font-bold text-danger">
          Off
        </span>
      );
    return (
      <span className="text-[10px] text-gray-400 dark:text-gray-600">—</span>
    );
  };

  // Count employees with special status
  const countSpecialStatus = () => {
    if (!scheduleData || !scheduleData.employees) return 0;

    return scheduleData.employees.filter((emp) => {
      const days = Object.values(emp.days || {});
      return days.some((day) => day !== "Office" && day !== "Holiday");
    }).length;
  };

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <RandomWFHModal
        isOpen={showRandomWFHModal}
        onClose={() => setShowRandomWFHModal(false)}
        weekStart={currentWeekStart}
        onSuccess={handleGenerateRandomWFH}
      />

      <PageWrapper
        title="Employees Schedule"
        subtitle="View approved requests in weekly schedule"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchSchedule(currentWeekStart)}
              disabled={loading}
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {isAdmin(employee) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowRandomWFHModal(true)}
                disabled={loading || !currentWeekStart}
              >
                <Shuffle className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Random WFH</span>
              </Button>
            )}
            <Button size="sm" onClick={handleAddRequest}>
              <Plus className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">Add Request</span>
            </Button>
          </div>
        }
      >
        {/* Header Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <p className="text-[14px] text-muted">
            <span className="font-display text-[16px] font-semibold text-ink">
              {countSpecialStatus()}
            </span>{" "}
            employees with approved requests
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center h-10 p-1 rounded-xl border border-line2 bg-panel">
              <button
                onClick={handlePreviousWeek}
                disabled={loading}
                aria-label="Previous week"
                className="h-8 w-8 rounded-lg text-muted hover:text-ink hover:bg-hover flex items-center justify-center disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleCurrentWeek}
                disabled={loading || isCurrentWeek()}
                className={`h-8 px-3 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors ${
                  isCurrentWeek() ? "bg-accent-soft text-accent-ink" : "text-muted hover:text-ink hover:bg-hover"
                }`}
              >
                Current week
              </button>
              <button
                onClick={handleNextWeek}
                disabled={loading}
                aria-label="Next week"
                className="h-8 w-8 rounded-lg text-muted hover:text-ink hover:bg-hover flex items-center justify-center disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            {scheduleData && (
              <input
                type="week"
                value={
                  currentWeekStart
                    ? `${currentWeekStart.getFullYear()}-W${String(Math.ceil((currentWeekStart.getDate() + currentWeekStart.getDay()) / 7)).padStart(2, "0")}`
                    : ""
                }
                onChange={(e) => {
                  const [year, week] = e.target.value.split("-W");
                  const newDate = new Date(year, 0, 1 + (week - 1) * 7);
                  const dayOfWeek = newDate.getDay();
                  const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
                  newDate.setDate(newDate.getDate() + diff);
                  setCurrentWeekStart(newDate);
                  fetchSchedule(newDate);
                }}
                className="h-10 font-mono text-[13px] text-muted px-3 bg-panel rounded-xl border border-line2 cursor-pointer hover:bg-hover transition-colors"
                disabled={loading}
              />
            )}
          </div>
        </div>

        {!loading && scheduleData && isAdmin(employee) && (() => {
          const total = scheduleData.employees?.length || 0;
          const pending = employeesWithoutWFH.length;
          const scheduled = Math.max(0, total - pending);
          const myId = String(employee?._id || employee?.id);
          const people = [...employeesWithoutWFH].sort(
            (a, b) => (String(b.employeeId) === myId) - (String(a.employeeId) === myId),
          );
          return (
            <div
              className="mb-5 p-5 flex flex-col gap-4 rounded-[20px] border border-line bg-panel shadow-card"
              style={{ backdropFilter: "blur(20px) saturate(1.3)" }}
            >
              <div className="flex flex-wrap items-center gap-3.5">
                <span className="w-10 h-10 rounded-xl bg-accent-soft flex items-center justify-center shrink-0">
                  <Home className="w-[19px] h-[19px] text-accent" />
                </span>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display text-[16px] font-semibold text-ink whitespace-nowrap">No WFH yet this week</p>
                  <p className="text-[12.5px] text-faint">
                    <b className="text-ink font-semibold">{pending}</b> of {total} employees still need a WFH day
                  </p>
                </div>
                <div style={{ width: "min(220px, 100%)" }}>
                  <div className="flex justify-between text-[12px] mb-1.5">
                    <span className="text-muted">Scheduled</span>
                    <span className="font-semibold text-ink tabular-nums">
                      {scheduled} / {total}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-raise overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-grad"
                      initial={{ width: 0 }}
                      animate={{ width: `${total ? (scheduled / total) * 100 : 0}%` }}
                      transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
                    />
                  </div>
                </div>
              </div>

              {pending === 0 ? (
                <p className="text-sm font-medium text-ok">
                  All employees have submitted Work From Home days for this week.
                </p>
              ) : (
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
                  {people.map((emp) => {
                    const isMe = String(emp.employeeId) === myId;
                    const name = emp.employeeName || "?";
                    return (
                      <div
                        key={emp.employeeId}
                        title={name}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl border cursor-pointer transition-all duration-150 hover:-translate-y-px hover:border-accent-line ${
                          isMe ? "bg-grad-soft border-accent-line" : "bg-panel2 border-line"
                        }`}
                      >
                        <Avatar src={emp.employeePhoto} name={name} className="!w-[30px] !h-[30px]" />
                        <span className="flex-1 min-w-0 text-[13px] font-medium text-ink truncate">{name}</span>
                        {isMe && (
                          <span className="px-[7px] py-0.5 rounded-full bg-grad text-on-accent text-[10.5px] font-bold">You</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Schedule Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-primary-accent animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-light-400">
                Loading schedule...
              </p>
            </div>
          </div>
        ) : !scheduleData ||
          !scheduleData.employees ||
          scheduleData.employees.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-gray-400 dark:text-light-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700 dark:text-light-300 mb-1">
                No employees found
              </p>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Add employees to see their schedule
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {scheduleData.employees.map((scheduleEmployee, index) => {
                const isCurrentUser =
                  scheduleEmployee.employeeId === employee?._id ||
                  scheduleEmployee.employeeId === employee?.id;
                return (
                  <motion.div
                    key={scheduleEmployee.employeeId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-panel backdrop-blur-xl rounded-2xl shadow-card border border-line overflow-hidden"
                  >
                    {/* Employee header */}
                    <div className="px-4 py-3 border-b border-line">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                          {scheduleEmployee.employeeName}
                        </span>
                        {isCurrentUser && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-primary-accent/10 text-primary-accent dark:bg-primary-accent/20 dark:text-primary-accent rounded-md">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-light-500">
                        {scheduleEmployee.employeeCode}
                      </div>
                    </div>
                    {/* Days grid */}
                    <div className="grid grid-cols-7 divide-x divide-[var(--line)]">
                      {scheduleData.days.map((day, dayIndex) => {
                        const dayKey = day.dayName
                          .toLowerCase()
                          .substring(0, 3);
                        const isHoliday = isDayHoliday(day.date, day.dayName);
                        const dayValue = scheduleEmployee.days[dayKey];
                        return (
                          <div
                            key={dayIndex}
                            className={`flex flex-col items-center justify-start py-2.5 px-0.5 gap-1 ${
                              columnTint(day, isHoliday)
                            }`}
                          >
                            <span
                              className={`text-[9px] font-bold uppercase ${
                                isHoliday
                                  ? "text-red-500 dark:text-red-400"
                                  : "text-gray-500 dark:text-light-500"
                              }`}
                            >
                              {day.dayName.substring(0, 3)}
                            </span>
                            <span
                              className={`text-xs font-bold ${
                                isHoliday
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-gray-900 dark:text-light-100"
                              }`}
                            >
                              {day.dayNumber}
                            </span>
                            {renderCellValueMobile(dayValue, isHoliday)}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto rounded-card border border-line bg-panel backdrop-blur-xl shadow-card">
              {/* Fixed layout: the employee column gets a set width and the 7
                  day columns share the rest equally. */}
              <table className="w-full min-w-[760px] table-fixed">
                <thead>
                  <tr>
                    <th className="w-[240px] px-5 py-4 text-left align-bottom text-xs border-b border-line">
                      Employee
                    </th>
                    {scheduleData.days.map((day, index) => {
                      const isHoliday = isDayHoliday(day.date, day.dayName);
                      const holidayName = getEgyptHolidayName(day.date);
                      return (
                        <th
                          key={index}
                          className={`px-4 py-4 text-center text-[11.5px] border-b border-line ${columnTint(day, isHoliday)}`}
                          style={{ color: day.date === todayKey ? "var(--accent-ink)" : isHoliday ? "var(--red)" : undefined }}
                        >
                          <div className="tracking-[0.06em]">{day.dayName.substring(0, 3).toUpperCase()}</div>
                          <div className="font-display text-[19px] font-semibold mt-0.5" style={{ color: day.date === todayKey ? "var(--accent)" : isHoliday ? "var(--red)" : "var(--text)" }}>
                            {day.dayNumber}
                          </div>
                          {isHoliday && (
                            <div className="text-[11px] truncate max-w-[110px] mx-auto" title={holidayName || undefined}>
                              {holidayName ? holidayName.slice(0, 14) : "Holiday"}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {scheduleData.employees.map((scheduleEmployee, index) => {
                    // Check if this is the current user
                    const isCurrentUser =
                      scheduleEmployee.employeeId === employee?._id ||
                      scheduleEmployee.employeeId === employee?.id;

                    return (
                      <motion.tr
                        key={scheduleEmployee.employeeId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-line last:border-b-0 hover:bg-hover transition-colors"
                      >
                        <td className="px-5 py-3 text-sm">
                          <div className="flex items-center gap-3">
                            <Avatar src={scheduleEmployee.employeePhoto} name={scheduleEmployee.employeeName} size="sm" />
                            <div className="min-w-0">
                              <div className="font-medium text-ink flex items-center gap-2">
                                <span className="truncate">{scheduleEmployee.employeeName}</span>
                                {isCurrentUser && (
                                  <span className="px-1.5 py-px text-[10.5px] font-semibold bg-grad text-on-accent rounded-full">You</span>
                                )}
                              </div>
                              <div className="font-mono text-[11.5px] text-faint">{scheduleEmployee.employeeCode}</div>
                            </div>
                          </div>
                        </td>
                        {scheduleData.days.map((day, dayIndex) => {
                          const dayKey = day.dayName
                            .toLowerCase()
                            .substring(0, 3); // sun, mon, tue, etc.
                          const isHoliday = isDayHoliday(day.date, day.dayName);
                          const dayValue = scheduleEmployee.days[dayKey];

                          return (
                            <td
                              key={dayIndex}
                              className={getCellStyle(dayValue, isHoliday, day)}
                            >
                              {renderCellValue(dayValue, isHoliday)}
                            </td>
                          );
                        })}
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PageWrapper>
    </>
  );
};

export default WeekSchedule;
